import { AppState } from './appState'
const mode = import.meta.env.MODE
const baseUrl = mode === 'production' ? '/api' : ''

export const ENDPOINT = {
  MORTGAGE_RATES: `${baseUrl}/interest-mortgage-service/api/v1/interest`,
  MORTGAGE_PRICE_MATRIX_DIFFERENCE: `${baseUrl}/interest-service/api/v1/interest/difference/market/mortgage`,
  PERSONAL_LOAN_EXAMPLE_RATE: `${baseUrl}/interest-personal-loans-service/api/external/v1/items`,
  PRODUCT_TERMS_MORTAGE:
    'https://api.sbab.se/api/product-terms/3.0/product/loan-application',
  PRODUCT_TERMS_PERSONAL_LOAN:
    'https://api.sbab.se/api/product-terms/3.0/product/personal-loan',
}

export const DEPOSIT_TO_VALUE_RATIO_LIMIT_AMORTIZATION_LEVEL1 = 50
export const DEPOSIT_TO_VALUE_RATIO_LIMIT_AMORTIZATION_LEVEL2 = 30
export const AMORTIZATION_LEVEL1 = 0.01
export const AMORTIZATION_LEVEL2 = 0.02
export const MAX_FULL_DEDUCTABLE_AMOUNT_PER_MONTH = 100_000 / 12

export const VILLA_OPERATING_COST = 4_500
export const CONDOMINIUM_OPERATING_COST = 200
export const RECREATIONAL_ESTATE_OPERATING_COST = 1_500

const defaultDepositPercentage = 15
export const DEFAULT_PROPERTY_VALUE = 1_500_000
export const DEFAULT_DEPOSIT = +(
  DEFAULT_PROPERTY_VALUE *
  (defaultDepositPercentage / 100)
).toFixed(0)

export const DEFAULT_LIST_INTERESTS = [
  {
    interestPeriod: 3,
    interestRate: '2.35',
    interestRateCreated: '2022-12-15T00:00:00',
    referenceId: 'LIS_BO_03M',
    termsEndDate: '2023-07-05',
    termsEndDateCreated: '2023-04-03T00:00:00.09',
  },
  {
    interestPeriod: 12,
    interestRate: '1.59',
    interestRateCreated: '2020-10-22T00:00:00',
    referenceId: 'LIS_BO_01Y',
    termsEndDate: '2024-04-05',
    termsEndDateCreated: '2023-04-03T00:00:00.09',
  },
  {
    interestPeriod: 24,
    interestRate: '1.91',
    interestRateCreated: '2022-02-22T00:00:00',
    referenceId: 'LIS_BO_02Y',
    termsEndDate: '2025-01-14',
    termsEndDateCreated: '2023-01-26T08:27:28.848',
  },
  {
    interestPeriod: 36,
    interestRate: '2.23',
    interestRateCreated: '2022-02-22T00:00:00',
    referenceId: 'LIS_BO_03Y',
    termsEndDate: '2026-01-14',
    termsEndDateCreated: '2023-01-26T08:27:28.848',
  },
  {
    interestPeriod: 48,
    interestRate: '2.00',
    interestRateCreated: '2022-04-05T00:00:00',
    referenceId: 'LIS_BO_04Y',
    termsEndDate: '2027-01-14',
    termsEndDateCreated: '2023-01-26T08:27:28.848',
  },
  {
    interestPeriod: 60,
    interestRate: '2.17',
    interestRateCreated: '2022-03-17T00:00:00',
    referenceId: 'LIS_BO_05Y',
    termsEndDate: '2028-01-14',
    termsEndDateCreated: '2023-01-26T08:27:28.848',
  },
  {
    interestPeriod: 84,
    interestRate: '2.64',
    interestRateCreated: '2022-02-22T00:00:00',
    referenceId: 'LIS_BO_07Y',
    termsEndDate: '2030-01-14',
    termsEndDateCreated: '2023-01-26T08:27:28.848',
  },
  {
    interestPeriod: 120,
    interestRate: '2.79',
    interestRateCreated: '2022-02-22T00:00:00',
    referenceId: 'LIS_BO_10Y',
    termsEndDate: '2033-01-14',
    termsEndDateCreated: '2023-01-26T08:27:28.848',
  },
]

export const DEFAULT_STATE: AppState = {
  houseObject: null,
  listInterests: DEFAULT_LIST_INTERESTS,
  propertyValue: DEFAULT_PROPERTY_VALUE,
  downPayment: DEFAULT_DEPOSIT,
  mortgageRateIndex: 0,
  hasAppError: false,
  hasCalculationError: false,
  errorMessage: '',
  monthlyFee: 4_500,
  yearlyOperatingCost: 20_000,
  personalLoanAmount: 0,
  personalLoanLengthInYears: 10,
  mortgageTerms: {
    maxDeposit: 800_000,
    maxLivingSpaceForCondo: 200,
    maxLivingSpaceForHouse: 300,
    maxLoanAmountForSecondaryHouse: 3_000_000,
    maxLoanAmountMortgage: 10_000_000,
    maxLoanAmountMortgageAndPersonalLoan: 10_000_000,
    maxLoanToValueRatioMortgage: 0.85,
    maxLoanToValueRatioMortgageAndPersonalLoan: 0.95,
    maxMaritalStatus: 3,
    maxTotalDebtForResidence: 10_000_000,
    minAgeForLoan: 18,
    minDeposit: 20_000,
    minDownPayment: 12_500,
    minHousePrice: 250_000,
    minLivingSpaceForCondo: 17,
    minLivingSpaceForHouse: 30,
    minLoanAmountForIncreasedMortgage: 50_000,
    minLoanAmountMortgage: 200_000,
    minMaritalStatus: 1,
    retirementAgeLimit: 60,
  },
  personalLoanTerms: {
    lenderList: [
      'Danske Bank',
      'Handelsbanken',
      'ICA Banken',
      'Ikano Banken',
      'Länsförsäkringar Bank',
      'Marginalen Bank',
      'Nordea',
      'Santander Consumer Bank',
      'SEB',
      'Skandiabanken',
      'Swedbank',
      'Annan',
    ],
    maxAmountOfMonthsRepaymentTime: 120,
    maxLoanAmountUnsecuredLoan: 500_000,
    maxMaritalStatus: 3,
    maxPersonalLoanAmount: 500_000,
    minAgeForPersonalLoan: 18,
    minAmountForRedemptionOfLoan: 30_000,
    minAmountOfMonthsRepaymentTime: 24,
    minAnnualIncomeMultipleApplicants: 225_000,
    minAnnualIncomeOneApplicant: 225_000,
    minMaritalStatus: 1,
    minPersonalLoanAmount: 30_000,
  },
  userIsChangingMortgageValues: false,
  interestDeviation: 0,
  personalLoanIsAdded: false,
  personalLoanExampleRate: 4.9,
}
